import type {
  AccountAdvanceLimits,
  AccountPreapproval,
  PreapprovalApplicationStatus,
} from "@/lib/brokerAccounts";
import { bondRatePercent, type BondProgramType } from "@/lib/brokerBonds";
import { formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import type { ReadAdvanceRequirements } from "@/lib/signalAdvanceEvidence";

// BRK-S401 — how the account's Current program enrollment block reads the
// published program, application and Signal Advance evidence.
//
// THREE STATES, the October wireframe's (BRK-S501), every one read from what
// PMS publishes:
//
//   • `none`    — no program in force: "Not enrolled", with "Enroll in Signal
//     Express" and "Enroll in Signal Advance →". `not_granted` reads exactly
//     like `none`: it covers a refusal AND a withdrawal (PMS-S307) and is
//     never a decline.
//   • `express` — an approved Signal Express program: "✓ Signal Express ·
//     Enrolled" and "Enroll in Signal Advance →".
//   • `advance` — an approved Signal Advance program: "✓ Signal Express ·
//     Included in Advance" and "Signal Advance · Enrolled".
//
// Two served facts change a BUTTON, never the state: a pre-approval
// application already open replaces "Enroll in Signal Express" with its own
// status line (BRK-S324 — no second application), and both Advance evidence
// requirements met on Express (`advanceRequirements.satisfied`, PMS-S722)
// reads "Signal Advance · In review" in place of the Advance enrol.
//
// THE FIGURES ARE SERVED. `terms` is the program IN FORCE; the Advance
// popover's "up to" figures are the program record's `advanceLimits`
// (PMS-S837). The SPA owns no number of its own, and nothing
// here explains a decision: no score, no tier, no reason
// (`preapprovalDiscretion.test.ts`).

export type EnrollmentState = "none" | "express" | "advance";

/** The status pill's colour, as a `statusBadgeClass` key. */
export type EnrollmentTone = "approved" | "neutral";

export interface ProgramEnrollmentInput {
  preapproval: AccountPreapproval;
  /** The current application, already read — null when there is none. */
  application: { status: PreapprovalApplicationStatus } | null;
  /** The Signal Advance evidence, already read — null when not published. */
  advance: ReadAdvanceRequirements | null;
}

export interface ProgramEnrollment {
  state: EnrollmentState;
  title: string;
  pill: string;
  tone: EnrollmentTone;
  /** The Express button's done label, or null when it offers enrolment. */
  expressChip: string | null;
  /** The Advance button's label when it is not an enrol action. */
  advanceChip: string | null;
  /** Whether "Enroll in Signal Advance →" is offered: not enrolled or on
   *  Express, and not while an Advance application is with Signal. */
  canEnrollAdvance: boolean;
}

const PROGRAM_NAME: Record<BondProgramType, string> = {
  express: "Signal Express",
  advance: "Signal Advance",
};

export function programEnrollment({
  preapproval,
  advance,
}: ProgramEnrollmentInput): ProgramEnrollment {
  const program =
    preapproval.status === "approved" ? preapproval.programType : null;
  if (program === "advance") {
    return {
      state: "advance",
      title: PROGRAM_NAME.advance,
      pill: "Approved",
      tone: "approved",
      expressChip: "Signal Express · Included in Advance",
      advanceChip: "Signal Advance · Enrolled",
      canEnrollAdvance: false,
    };
  }
  const advanceInReview = advance?.satisfied === true;
  const advanceButton = {
    advanceChip: advanceInReview ? "Signal Advance · In review" : null,
    canEnrollAdvance: !advanceInReview,
  };
  if (program === "express") {
    return {
      state: "express",
      title: PROGRAM_NAME.express,
      pill: "Approved",
      tone: "approved",
      expressChip: "Signal Express · Enrolled",
      ...advanceButton,
    };
  }
  return {
    state: "none",
    title: "Not enrolled",
    pill: "Not enrolled",
    tone: "neutral",
    expressChip: null,
    ...advanceButton,
  };
}

export interface ProgramInfoStat {
  label: string;
  value: string;
}

export interface ProgramInfo {
  title: string;
  tag: string;
  points: readonly string[];
  /** The insured's own figures for this program — empty unless they hold it. */
  stats: ProgramInfoStat[];
}

/** What each program IS, in words. No figure and no score lives here. */
const PROGRAM_COPY: Record<
  BondProgramType,
  { tag: string; points: readonly string[] }
> = {
  express: {
    tag: "Credit-based",
    points: ["Approval from a credit pull on the business principal"],
  },
  advance: {
    tag: "Financials-based",
    points: [
      "Lower rates than Signal Express",
      "Connect bank and accounting software once",
      "Signal review confirms the program rate",
    ],
  },
};

/**
 * One program's info popover: its name, what it is, and its figures — the
 * insured's own `terms` for the program they HOLD, else, for Advance, the
 * program record's "up to" limits (`advanceLimits`). A figure that is not
 * served is omitted, never filled in.
 */
export function programInfo(
  program: BondProgramType,
  preapproval: AccountPreapproval,
  advanceLimits?: AccountAdvanceLimits | null,
): ProgramInfo {
  const held =
    preapproval.status === "approved" && preapproval.programType === program
      ? preapproval.terms
      : null;
  return {
    title: PROGRAM_NAME[program],
    ...PROGRAM_COPY[program],
    stats: held
      ? programFigures(held)
      : program === "advance" && advanceLimits
        ? advanceOfferFigures(advanceLimits)
        : [],
  };
}

/** The program record's Advance limits, as "Earn up to" figures. */
export function advanceOfferFigures(
  limits: AccountAdvanceLimits,
): ProgramInfoStat[] {
  return [
    {
      label: "Per bond limit",
      value: formatPreapprovalMoney(limits.singleBondLimit),
    },
    {
      label: "Aggregate bonding capacity",
      value: formatPreapprovalMoney(limits.aggregateProgramLimit),
    },
  ];
}

/** The three figures, as the block and the popover both render them. The rate
 *  is stored per $1,000 and shown as a percent (`bondRatePercent`). */
export function programFigures(
  terms: NonNullable<AccountPreapproval["terms"]>,
): ProgramInfoStat[] {
  return [
    {
      label: "Single bond limit",
      value: formatPreapprovalMoney(terms.singleBondLimit),
    },
    {
      label: "Aggregate program limit",
      value: formatPreapprovalMoney(terms.aggregateProgramLimit),
    },
    {
      label: "Bond rate",
      value: `${bondRatePercent(terms.ratePer1000)}% flat rate`,
    },
  ];
}
